import { useEffect, useRef, useMemo, useCallback, SyntheticEvent } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { treezorLocaleConverters, DateTools } from '@treezor/design-system';

import { IUser, IPutUser } from '@/features/user/store/user.model';
import { updateUser } from '@/features/user/store/user.actions';

import { useAppDispatch, useAppSelector } from '@/hooks/redux-helpers';
import { useDashboardConfig } from '@/hooks/useDashboardConfig';
import { useI18nLocale } from '@/hooks/useI18nLocale';

import { selectProfileByUserId } from '@/store/selectors';

type TPutUser = IPutUser & {
  middleName1?: string;
  middleName2?: string;
};

export function useUpdateUser(user: IUser) {
  const { t } = useTranslation();
  const config = useDashboardConfig();
  const dispatch = useAppDispatch();
  const locale = useI18nLocale();
  const language = treezorLocaleConverters[locale];

  const distributionCountry = config?.settings?.users?.distributionCountry;

  const isLoading = useAppSelector((state) => selectProfileByUserId(state, user!.userId)?.state?.user.isLoading);

  const previousUserRef = useRef<IUser | undefined>(undefined);

  const defaultValues = useMemo(() => {
    const { modifiedDate, ...rest } = user;
    const [middleName1, ...otherMiddleNames] = user.middleNames.split(', ');
    const middleName2 = otherMiddleNames.join(', ');
    return {
      ...rest,
      middleName1,
      middleName2,
      legalRegistrationDate: user.legalRegistrationDate
        ? DateTools.format(user.legalRegistrationDate, 'date', locale)
        : undefined,
      birthday: user.birthday ? DateTools.format(user.birthday, 'date', locale) : undefined,
    };
  }, [user, locale]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { touchedFields },
  } = useForm<TPutUser>();

  useEffect(() => {
    if (user !== previousUserRef.current) {
      reset(defaultValues);
      previousUserRef.current = user;
    }
  }, [user, defaultValues, reset]);

  const handleValueChange = useCallback(
    (value: string | number | boolean, field: keyof TPutUser) => {
      setValue(field, value);
    },
    [setValue],
  );

  const handleInputChange = useCallback(
    (event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>, field: keyof TPutUser) => {
      const value = event.currentTarget.value;
      handleValueChange(value, field);
    },
    [handleValueChange],
  );

  const inputMaxNumber = useCallback(
    (inputNumber: number, maxNumber: number) => ({
      maxNumber,
      error:
        inputNumber > maxNumber ? t('pages.dashboard.createUser.form.rules.maxNumber', { max: maxNumber }) : undefined,
    }),
    [t],
  );

  const inputMaxLength = useCallback(
    (inputValue: string, maxLengthValue: number) => ({
      maxLengthValue,
      error:
        inputValue?.length > maxLengthValue ? t('pages.dashboard.createUser.form.rules.addressMaxLength') : undefined,
    }),
    [t],
  );

  const hasValidField = useCallback(
    (inputFields: string[]) => (inputFields || []).some((field: keyof TPutUser) => !!watch(field)),
    [watch],
  );

  const hasInvalidInputLength = useCallback(
    (inputFields: string[]) =>
      inputFields.some((field: keyof TPutUser) => !!inputMaxLength(watch(field) as string, 35)?.error),
    [watch, inputMaxLength],
  );

  const onUpdateUser = useCallback(
    (toogleSectionEditMode?: () => void) => {
      const { middleName1, middleName2, legalRegistrationDate, birthday, ..._values } = watch();

      const values: IPutUser = {
        ..._values,
        middleNames: middleName1 || middleName2 ? [middleName1, middleName2].filter(Boolean).join(', ') : '',
        legalRegistrationDate: legalRegistrationDate
          ? DateTools.format(DateTools.localStringToDate(legalRegistrationDate, language), 'api-date')
          : undefined,
        birthday: birthday ? DateTools.format(DateTools.localStringToDate(birthday, language), 'api-date') : undefined,
      };

      const userFormatted: Partial<IPutUser> = Object.entries(values).reduce((acc, [key, value]) => {
        if (value !== user[key]) {
          if (
            [
              'birthCountry',
              'nationality',
              'nationalityOther',
              'sourceOfFunds',
              'legalNumberOfEmployeeRange',
              'country',
              'secondaryCountry',
              'occupation',
              'personalAssets',
              'title',
              'legalForm',
            ].includes(key)
          )
            return { ...acc, [key]: value ?? '' };
          if (['monthlyIncomeRange', 'occupationCategory', 'personalAssetsRange'].includes(key))
            return { ...acc, [key]: value ?? null };
          if (['entityType', 'entitySanctionsQuestionnaire'].includes(key)) return { ...acc, [key]: value ?? 0 };
          else return { ...acc, [key]: value };
        }
        return acc;
      }, {});

      dispatch(updateUser(userFormatted, user.userId, () => toogleSectionEditMode?.()));
    },
    [dispatch, watch, language, user],
  );

  const onCancelEdition = useCallback(
    (toogleSectionEditMode: () => void) => {
      reset();
      toogleSectionEditMode();
    },
    [reset],
  );

  const handlePrefillData = useCallback(
    (data: IPutUser) => {
      Object.entries(data || {}).forEach(([key, value]) => {
        if (value !== undefined) {
          handleValueChange(value, key as keyof IPutUser);
        }
      });
    },
    [handleValueChange],
  );

  const handleDateChange = useCallback(
    (date: Date, field: keyof IPutUser) =>
      setValue(field, date ? DateTools.dateToLocalDateString(date, language) : undefined),
    [language, setValue],
  );

  return {
    register,
    handleSubmit,
    watch,
    setValue,
    touchedFields,
    inputMaxLength,
    user,
    updateUser,
    handleInputChange,
    handleValueChange,
    isLoading,
    distributionCountry,
    hasValidField,
    hasInvalidInputLength,
    inputMaxNumber,
    onUpdateUser,
    language,
    handlePrefillData,
    handleDateChange,
    onCancelEdition,
  };
}
